import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  CreditCard,
  LogIn,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "./ui/Button";
import { GoogleGlyph } from "./ui/GoogleGlyph";
import {
  PREMIUM_FEATURES,
  usePremium,
  type PremiumFeatureKey, hadTrial } from "../lib/premium";
import {
  annualPerMonthUnitAmount,
  annualSavingsPercent,
  fetchPremiumPlan,
  formatInterval,
  formatPrice,
  planPricesFor,
  type BillingInterval,
  hasManageableSubscription,
  openCustomerPortal,
  PremiumCheckoutNoProductError,
  PremiumCheckoutSignInRequiredError,
  PremiumCheckoutUnavailableError,
  startPremiumCheckout,
  type PremiumPlan,
} from "../lib/stripeCheckout";
import { AuthError, signInWithGoogle, useAuth, hasAccount } from "../lib/auth";
import { BILLING_CONFIG, formatUsd } from "../lib/billingConfig";
import { track } from "../lib/telemetry";
import type { UpsellTrigger } from "../lib/events";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Which feature the user just tried to use. Drives the headline + blurb
   *  so the overlay reads like a contextual unlock rather than a generic
   *  "subscribe" pitch. */
  feature: PremiumFeatureKey;
  /** What opened the overlay, for the upsell funnel. */
  trigger?: UpsellTrigger;
}

type DismissMethod = "close_button" | "backdrop" | "escape" | "maybe_later";

const SIGN_IN_TITLE = "Sign in to use this feature";

/** Marketing bullets shown in the overlay's feature list. Intentionally
 *  separate from {@link PREMIUM_FEATURES} (the gating registry): we split
 *  the AI feng-shui feature into two bullets (generation + scoring) here
 *  because users perceive them as distinct value props, even though they
 *  unlock under the same `ai_layout_feng_shui` gating key.
 *
 *  After the usage-based migration each bullet also describes how the
 *  shared credit pool maps to its feature — the goal is to make it
 *  obvious that a Premium subscription unlocks ALL three at the same
 *  shared monthly allowance, not three separate quotas. */
const UPSELL_FEATURES: readonly {
  label: string;
  blurb: string;
  /** Leads the list when this feature opened the overlay. */
  leadFor?: PremiumFeatureKey;
}[] = [
  {
    label: "Unlimited shared rooms",
    blurb: `Share every room and design with up to ${BILLING_CONFIG.premiumRoomCollaborators} people, live.`,
    leadFor: "collab_unlimited",
  },
  {
    label: "Feng Shui Layout Generator",
    blurb: "Auto-arrange your room with reasoning from classical feng shui.",
    leadFor: "ai_layout_feng_shui",
  },
  {
    label: "Layout Score",
    blurb: "Instantly score and critique the room you already drew.",
  },
  {
    label: "Product URL Import",
    blurb: "Paste a listing URL to drop the item onto your canvas.",
    leadFor: "product_url_import",
  },
];

/** The triggering feature's bullet first, the rest in their usual order. */
function orderedFeatures(feature: PremiumFeatureKey) {
  const lead = UPSELL_FEATURES.filter((f) => f.leadFor === feature);
  return [...lead, ...UPSELL_FEATURES.filter((f) => f.leadFor !== feature)];
}

/**
 * Modal overlay shown when the user clicks a Premium feature without a
 * Premium entitlement. Three states based on auth:
 *
 *   - signed out → a short sign-in ask with no price or plan (the feature
 *     needs an account; signing up is free). After the sign-in the overlay
 *     re-renders into one of the next two states.
 *   - signed in, not premium → primary CTA = "Upgrade to Premium" (writes
 *     a `checkout_sessions` doc, the Stripe extension fills in `url`,
 *     the browser redirects). Secondary "Manage subscription" link if an
 *     existing inactive subscription is on file.
 *   - signed in, already premium → the overlay auto-closes (callers should
 *     never open it in this state, but a stale render after a fresh
 *     subscription unlock would otherwise look broken).
 *
 * The overlay is portal-rendered for the same reason {@link import("./ui/Sheet").Sheet}
 * is — it has to escape any z-index / overflow context the host view
 * creates around the canvas.
 */
export function PremiumUpsellOverlay({ open, onClose, feature, trigger = "manual" }: Props) {
  const auth = useAuth();
  const premium = usePremium();
  const def = PREMIUM_FEATURES[feature];
  const signedIn = auth.status === "signed-in" && hasAccount(auth.user);
  // A finished reverse trial reads differently from "never had Premium".
  const trialEnded = signedIn && !premium.isPremium && hadTrial(auth.user?.uid);

  // One `upsell_shown` per opening. Signed-out visitors also see a
  // sign-in CTA here, so the sign-in funnel counts it too.
  useEffect(() => {
    if (!open || premium.isPremium) return;
    track("upsell_shown", { feature, signed_in: signedIn, trigger });
    if (!signedIn && auth.status !== "unavailable") {
      track("sign_in_prompt_shown", { surface: "upsell" });
    }
    // Deliberately keyed on `open` only: a sign-in inside the overlay
    // must not count as a second impression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = useCallback(
    (method: DismissMethod) => {
      track("upsell_dismissed", { feature, trigger, method });
      onClose();
    },
    [feature, trigger, onClose]
  );

  // Auto-close once the user actually has premium. Covers the case where
  // the user upgrades, returns from checkout, and the overlay was still
  // open in the background.
  useEffect(() => {
    if (open && premium.isPremium) onClose();
  }, [open, premium.isPremium, onClose]);

  // Esc-to-close + body scroll lock — same pattern as Sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss("escape");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // null: loading (haven't fetched yet OR fetch in flight).
  // PremiumPlan: resolved {base, metered?} pair.
  // false: fetched but no usable plan (catalog unconfigured or
  //   ambiguous). Distinguished from `null` so we can render the
  //   "not configured" message vs. the loading shimmer.
  const [plan, setPlan] = useState<PremiumPlan | null | false>(null);
  // Yearly leads when the catalog offers it: the anchored per-month
  // number is what the price block shows first.
  const [interval, setInterval] = useState<BillingInterval>("year");
  const [canManage, setCanManage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch plan + portal eligibility once when the overlay opens. We
  // re-fetch on each open in case the user just upgraded, downgraded, or
  // an admin updated pricing — these are tiny reads (one product + a few
  // prices) so the cost is negligible.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setPlan(null);
    void fetchPremiumPlan().then((next) => {
      if (!cancelled) setPlan(next ?? false);
    });
    if (auth.status === "signed-in" && hasAccount(auth.user)) {
      void hasManageableSubscription().then((v) => {
        if (!cancelled) setCanManage(v);
      });
    } else {
      setCanManage(false);
    }
    return () => {
      cancelled = true;
    };
  }, [open, auth.status]);

  const chosen = useMemo(() => (plan ? planPricesFor(plan, interval) : null), [plan, interval]);
  const leadPrice = chosen?.base ?? null;
  const annualOffered = !!plan && plan.annual !== null;

  async function handleSignIn() {
    setError(null);
    setSigningIn(true);
    try {
      await signInWithGoogle("upsell");
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.kind === "redirecting") return;
        if (err.kind === "cancelled") {
          setSigningIn(false);
          return;
        }
        setError(err.message);
      } else {
        setError((err as Error).message || "Sign-in failed.");
      }
    } finally {
      setSigningIn(false);
    }
  }

  async function handleUpgrade() {
    if (!plan) {
      setError(new PremiumCheckoutNoProductError().message);
      return;
    }
    setError(null);
    setBusy(true);
    track("upsell_upgrade_clicked", { feature, trigger });
    try {
      // Send both prices when a metered price exists — Stripe creates a
      // single subscription with two items (base flat + metered overage)
      // bound to the same billing cycle. Falls back to single-price for
      // the legacy / half-migrated catalog. The metered line item is
      // wired in without a quantity (required by Stripe for metered
      // prices) — startPremiumCheckout handles that.
      const prices = planPricesFor(plan, interval);
      await startPremiumCheckout({
        priceId: prices.base.id,
        meteredPriceId: prices.metered?.id,
        analytics: { base: prices.base, feature },
      });
      // Promise effectively never resolves — the page is navigating away
      // to Stripe Checkout. Leave `busy` true so the button stays disabled
      // until the navigation completes.
    } catch (err) {
      setBusy(false);
      if (err instanceof PremiumCheckoutSignInRequiredError) {
        setError(err.message);
        return;
      }
      if (err instanceof PremiumCheckoutUnavailableError) {
        setError(err.message);
        return;
      }
      setError((err as Error).message || "Couldn't start checkout.");
    }
  }

  async function handleManage() {
    setError(null);
    setBusy(true);
    track("upsell_manage_clicked", { feature });
    try {
      await openCustomerPortal();
    } catch (err) {
      setBusy(false);
      setError((err as Error).message || "Couldn't open the customer portal.");
    }
  }

  if (typeof document === "undefined") return null;

  // `plan === null` is the loading state; `plan === false` means we
  // resolved but the catalog isn't configured.
  const planLoading = plan === null;
  const stripeConfigured = plan !== null;
  const hasLeadPrice = leadPrice !== null;
  const hasMetered = !!chosen && chosen.metered !== null;
  const errorNote = error ? (
    <div
      className="mt-4 p-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 text-sm text-rose-800"
      data-testid="premium-upsell-error"
    >
      {error}
    </div>
  ) : null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => dismiss("backdrop")}
            className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-[2px]"
          />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center px-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            role="dialog"
            aria-modal="true"
            aria-label={signedIn ? def.headline : SIGN_IN_TITLE}
            data-testid="premium-upsell-overlay"
            className="pointer-events-auto relative flex max-h-full w-full flex-col md:w-[min(440px,92vw)] bg-white rounded-3xl shadow-2xl shadow-ink-900/30 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => dismiss("close_button")}
              aria-label="Close"
              className={`absolute top-2 right-2 h-11 w-11 inline-flex items-center justify-center rounded-full z-10 ${
                signedIn ? "text-white/90 hover:bg-white/15" : "text-ink-500 hover:bg-ink-100"
              }`}
            >
              <X className="size-5" />
            </button>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {!signedIn ? (
              <div className="px-6 pt-7 pb-5" data-testid="premium-upsell-signin-ask">
                <div className="grid size-11 place-items-center rounded-2xl bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                  <LogIn className="size-5" aria-hidden />
                </div>
                <h2 className="mt-4 text-xl font-bold leading-tight text-ink-900">{SIGN_IN_TITLE}</h2>
                <p className="mt-1.5 text-sm leading-snug text-ink-600">
                  {def.signIn} Signing up is free.
                </p>
                {errorNote}
              </div>
            ) : (
            <>
            <div className="bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500 text-white px-6 pt-6 pb-5">
              <div className="inline-flex items-center gap-1.5 text-label uppercase tracking-wider font-semibold bg-white/15 rounded-full px-2.5 py-1">
                <Sparkles className="size-3" />
                Premium
              </div>
              <h2 className="mt-3 text-2xl font-bold leading-tight">
                {def.headline}
              </h2>
              <p className="mt-1.5 text-sm text-white/85 leading-snug">
                {def.blurb}
              </p>
              {trialEnded && (
                <p className="mt-2 text-sm font-medium text-white" data-testid="premium-trial-ended">
                  Your free week of Premium has ended — keep it going below.
                </p>
              )}
            </div>

            <div className="px-6 pt-5 pb-6">
              <ul className="flex flex-col gap-2.5">
                {orderedFeatures(feature).map((f) => (
                  <li key={f.label} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-5 text-violet-600 shrink-0 mt-px" />
                    <div>
                      <div className="text-body-sm font-semibold text-ink-900">
                        {f.label}
                      </div>
                      <div className="text-caption text-ink-500 leading-snug">
                        {f.blurb}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-5 rounded-2xl bg-ink-50 ring-1 ring-ink-100 p-4">
                {annualOffered && plan && plan.annual && (
                  <div
                    className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-white p-1 ring-1 ring-ink-200"
                    role="radiogroup"
                    aria-label="Billing interval"
                    data-testid="premium-interval-toggle"
                  >
                    {(["year", "month"] as const).map((opt) => {
                      const active = interval === opt;
                      const save = annualSavingsPercent(plan.base, plan.annual!.base);
                      return (
                        <button
                          key={opt}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setInterval(opt)}
                          className={`h-10 rounded-lg text-sm font-semibold transition-colors ${
                            active ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-ink-50"
                          }`}
                          data-testid={`premium-interval-${opt}`}
                        >
                          {opt === "year" ? (
                            <>
                              Yearly
                              {save > 0 && (
                                <span
                                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[0.65rem] ${
                                    active ? "bg-emerald-400/20 text-emerald-200" : "bg-emerald-50 text-emerald-700"
                                  }`}
                                >
                                  save {save}%
                                </span>
                              )}
                            </>
                          ) : (
                            "Monthly"
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {hasLeadPrice ? (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className="text-2xl font-bold text-ink-900 tabular-nums"
                        data-testid="premium-price-amount"
                      >
                        {leadPrice.interval === "year"
                          ? formatPrice(annualPerMonthUnitAmount(leadPrice), leadPrice.currency)
                          : formatPrice(leadPrice.unitAmount, leadPrice.currency)}
                      </span>
                      <span className="text-sm text-ink-500">
                        {leadPrice.interval === "year"
                          ? `/ month · ${formatPrice(leadPrice.unitAmount, leadPrice.currency)} billed yearly`
                          : formatInterval(leadPrice.interval)}
                      </span>
                      {leadPrice.trialPeriodDays ? (
                        <span className="ml-auto text-caption font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2 py-0.5">
                          {leadPrice.trialPeriodDays}-day trial
                        </span>
                      ) : null}
                    </div>
                    {hasMetered ? (
                      <div
                        className="mt-2 text-caption text-ink-700 leading-snug"
                        data-testid="premium-credits-copy"
                      >
                        Includes{" "}
                        <span className="font-semibold tabular-nums">
                          {BILLING_CONFIG.premiumIncludedCredits.toLocaleString()}
                        </span>{" "}
                        credits / month (Feng Shui layout, room score, or
                        product import — your choice{leadPrice.interval === "year" ? ", pooled across the year" : ""}). Beyond that,{" "}
                        <span className="font-semibold">
                          {formatUsd(BILLING_CONFIG.overagePriceUsdPerCredit)}
                        </span>{" "}
                        per extra credit, billed on your next invoice.
                      </div>
                    ) : (
                      <div className="mt-2 text-caption text-ink-700 leading-snug">
                        Unlocks every feature listed above.
                      </div>
                    )}
                  </>
                ) : planLoading ? (
                  <div className="text-sm text-ink-500">Loading pricing…</div>
                ) : (
                  <div className="text-sm text-ink-500">
                    Pricing isn't configured yet. Check back soon.
                  </div>
                )}
                <div className="mt-1.5 text-caption text-ink-400">
                  Cancel anytime. Premium stays through the end of the current
                  billing period, and extra credits are billed on the final
                  invoice. Powered by Stripe.
                </div>
              </div>

              {errorNote}
            </div>
            </>
            )}
            </div>

            <div className="shrink-0 border-t border-ink-100 bg-white px-6 pt-3 pb-4">
              <div className="flex flex-col gap-1.5">
                {!signedIn ? (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSignIn}
                    disabled={signingIn || auth.status === "unavailable"}
                    data-testid="premium-upsell-signin"
                  >
                    <GoogleGlyph className="size-5 rounded-full bg-white p-0.5" />
                    {signingIn ? "Signing in…" : "Sign in with Google to continue"}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleUpgrade}
                    disabled={busy || !hasLeadPrice || !stripeConfigured}
                    data-testid="premium-upsell-upgrade"
                  >
                    <CreditCard className="size-4" />
                    {busy ? "Opening checkout…" : "Upgrade to Premium"}
                  </Button>
                )}
                {signedIn && canManage && (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={handleManage}
                    disabled={busy}
                    data-testid="premium-upsell-manage"
                  >
                    Manage subscription
                  </Button>
                )}
                <Button variant="ghost" size="md" onClick={() => dismiss("maybe_later")}>
                  Maybe later
                </Button>
              </div>

              {auth.status === "unavailable" && (
                <p className="mt-2 text-xs text-ink-400 text-center">
                  Sign-in isn't configured for this environment.
                </p>
              )}
              {signedIn && stripeConfigured && plan === false && (
                <p
                  className="mt-2 text-xs text-ink-400 text-center"
                  data-testid="premium-upsell-no-stripe"
                >
                  The store isn't configured yet — talk to the project
                  owner to enable purchases.
                </p>
              )}
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

